import { addDays, maxDate, minDate, type IsoDate } from './dates';
import { purchaseConversion } from './lotSizing';
import type { PlannedOrder, PlanningModel, PlanningSupplier } from './types';

export interface PoDraftLine {
  order: PlannedOrder;
  /** Stock units the line adds. */
  quantity: number;
  /** Per stock unit. */
  unitCostCents: number | null;
  /** What the PO line says: quantity and price in the supplier's purchase unit. */
  purchaseQuantity: number;
  purchaseUom: string | null;
  /** Stock units per purchase unit. */
  conversion: number;
  purchaseUnitCostCents: number | null;
  /** True when the line is bumped up to the price-break quantity. */
  usingPriceBreak: boolean;
}

export interface PoDraft {
  vendorId: string;
  vendorName: string;
  supplier: PlanningSupplier;
  /** Earliest "last safe day" across the lines — the PO's deadline. */
  orderByDate: IsoDate;
  /** Earliest ideal order date across the lines. */
  idealOrderDate: IsoDate;
  /** Typical arrival if placed today, driven by the slowest line. */
  expectedAt: IsoDate | null;
  leadTimeDays: number | null;
  lines: PoDraftLine[];
  totalCents: number;
  linesWithoutCost: number;
}

export interface PoDraftOptions {
  /** Only orders whose ideal date is within this many days (null = all). */
  dueWithinDays: number | null;
  /** Order ids to bump to their price-break quantity. */
  usePriceBreakFor: ReadonlySet<string>;
  /** Order ids the user unticked. */
  excluded: ReadonlySet<string>;
}

export interface PoDraftResult {
  drafts: PoDraft[];
  /** Orders with no vendor on file — nothing to put a PO against. */
  unassignable: PlannedOrder[];
}

/**
 * Group the planner's suggested orders into one draft PO per vendor.
 *
 * The PO's `order_by_date` is the earliest latest-order date of its
 * lines: once that day passes, at least one part on it will be late.
 */
export function buildPoDrafts(model: PlanningModel, options: PoDraftOptions): PoDraftResult {
  const { today } = model;
  const cutoff = options.dueWithinDays == null ? null : addDays(today, options.dueWithinDays);
  const byVendor = new Map<string, PoDraft>();
  const unassignable: PlannedOrder[] = [];

  for (const order of model.orders) {
    if (options.excluded.has(order.id)) continue;
    const ideal = order.idealOrderDate ?? today;
    if (cutoff != null && ideal > cutoff) continue;
    if (!order.supplier) {
      unassignable.push(order);
      continue;
    }

    const usingPriceBreak = options.usePriceBreakFor.has(order.id) && order.priceBreakOption != null;
    const quantity = usingPriceBreak ? order.priceBreakOption!.quantity : order.quantity;
    const purchaseQuantity = usingPriceBreak
      ? order.priceBreakOption!.purchaseQuantity
      : order.purchaseQuantity;
    const unitCostCents = usingPriceBreak ? order.priceBreakOption!.unitCostCents : order.unitCostCents;
    const conversion = purchaseConversion(order.supplier);
    const line: PoDraftLine = {
      order,
      quantity,
      unitCostCents,
      purchaseQuantity,
      purchaseUom: conversion !== 1 ? order.supplier.purchaseUom : null,
      conversion,
      purchaseUnitCostCents:
        unitCostCents != null ? Math.round(unitCostCents * conversion * 10_000) / 10_000 : null,
      usingPriceBreak,
    };

    const latest = order.latestOrderDate ?? ideal;
    const existing = byVendor.get(order.supplier.vendorId);
    if (existing) {
      existing.lines.push(line);
      existing.orderByDate = minDate(existing.orderByDate, maxDate(latest, today));
      existing.idealOrderDate = minDate(existing.idealOrderDate, maxDate(ideal, today));
      if (order.typicalLeadDays != null) {
        existing.leadTimeDays = Math.max(existing.leadTimeDays ?? 0, order.typicalLeadDays);
      }
    } else {
      byVendor.set(order.supplier.vendorId, {
        vendorId: order.supplier.vendorId,
        vendorName: order.supplier.vendorName,
        supplier: order.supplier,
        orderByDate: maxDate(latest, today),
        idealOrderDate: maxDate(ideal, today),
        expectedAt: null,
        leadTimeDays: order.typicalLeadDays,
        lines: [line],
        totalCents: 0,
        linesWithoutCost: 0,
      });
    }
  }

  const drafts = [...byVendor.values()].map((draft) => {
    let total = 0;
    let missing = 0;
    for (const line of draft.lines) {
      if (line.purchaseUnitCostCents == null) missing += 1;
      else total += Math.round(line.purchaseUnitCostCents * line.purchaseQuantity);
    }
    return {
      ...draft,
      lines: draft.lines.sort((a, b) =>
        (a.order.idealOrderDate ?? '').localeCompare(b.order.idealOrderDate ?? ''),
      ),
      expectedAt:
        draft.leadTimeDays != null
          ? addDays(today, draft.leadTimeDays + model.settings.receivingBufferDays)
          : null,
      totalCents: total,
      linesWithoutCost: missing,
    };
  });

  drafts.sort((a, b) => a.orderByDate.localeCompare(b.orderByDate) || a.vendorName.localeCompare(b.vendorName));
  return { drafts, unassignable };
}
